import _ from "lodash";

import DateService from "core/DateService";

type ValidationResult = undefined | string;
type ValidatorFunction = Function;
type LanguageObject = Record<string, any>;

class Validation {
  private successReturnValue: any = undefined;
  private templateInterpolation: RegExp = /\{([a-zA-Z0-9_-]+)\}/g;
  private lang: LanguageObject = {};

  setTemplateInterpolation = (
    regex: RegExp = this.templateInterpolation
  ): void => {
    this.templateInterpolation = regex;
  };

  translator = (
    str: string = "",
    contentVariables?: LanguageObject
  ): string => {
    if (_.isNil(contentVariables)) {
      return str;
    }
    _.templateSettings.interpolate = this.templateInterpolation;
    const compiled = _.template(str);
    return compiled(contentVariables);
  };

  setLanguageObject = (langObject: LanguageObject): void => {
    this.lang = langObject;
  };

  setSuccessReturnValue = (value: any): void => {
    this.successReturnValue = value;
  };

  returnErrorMessage = (
    langKey: string,
    customMsg?: string,
    translationVariables: LanguageObject = {}
  ): string => {
    const msg = _.defaultTo(
      customMsg,
      _.get(this.lang, langKey, "default field error")
    );
    return this.translator(msg, translationVariables);
  };

  returnValidationResult = (
    isValid: boolean,
    langKey: string,
    customMsg?: string,
    translationVariables?: LanguageObject
  ): ValidationResult => {
    const errorMessage = this.returnErrorMessage(
      langKey,
      customMsg,
      translationVariables
    );
    return isValid ? this.successReturnValue : errorMessage;
  };

  required = (customMsg?: string): ValidatorFunction => {
    return (value?: string | any[] | boolean) => {
      const isValid = !(
        _.isNil(value) ||
        (_.isArray(value) && _.isEmpty(value)) ||
        (_.isBoolean(value) && !value) ||
        (_.isString(value) && _.isEmpty(value))
      );
      return this.returnValidationResult(isValid, "required", customMsg);
    };
  };

  pattern = (pattern: RegExp, customMsg?: string): ValidatorFunction => {
    return (value?: string) => {
      if (!value) {
        return this.returnValidationResult(true, "pattern", customMsg);
      }
      const isValid = pattern.test(value);
      return this.returnValidationResult(isValid, "pattern", customMsg);
    };
  };

  min = (min: number, customMsg?: string): ValidatorFunction => {
    return (value?: string | number) => {
      const isValid = Number(value) >= Number(min);
      return this.returnValidationResult(isValid, "min", customMsg, { min });
    };
  };

  max = (max: number, customMsg?: string): ValidatorFunction => {
    return (value?: string | number) => {
      const isValid = Number(value) <= Number(max);
      return this.returnValidationResult(isValid, "max", customMsg, { max });
    };
  };

  minLength = (minLength: number, customMsg?: string): ValidatorFunction => {
    return (value?: string) => {
      const isValid = _.size(value) >= Number(minLength);
      return this.returnValidationResult(isValid, "minLength", customMsg, {
        minLength
      });
    };
  };

  maxLength = (maxLength: number, customMsg?: string): ValidatorFunction => {
    return (value?: string) => {
      const isValid = _.size(value) <= Number(maxLength);
      return this.returnValidationResult(isValid, "maxLength", customMsg, {
        maxLength
      });
    };
  };

  email = (customMsg?: string): ValidatorFunction => {
    return (value?: string) => {
      const emailPattern =
        /* eslint-disable-next-line */
        /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
      const isValid = emailPattern.test(value || "");
      return this.returnValidationResult(isValid, "email", customMsg);
    };
  };

  emailWithAccent = (customMsg?: string): ValidatorFunction => {
    return (value?: string) => {
      const emailWithAccentPattern =
        /* eslint-disable-next-line */
        /(?:[áéíóúüña-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[áéíóúüña-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
      const isValid = emailWithAccentPattern.test(value || "");
      return this.returnValidationResult(isValid, "email", customMsg);
    };
  };

  /**
   * Strict email validation for all providers.
   * Validates:
   * - Exactly one @ symbol
   * - @ not at start or end
   * - No spaces
   * - No control characters (\n, \t, \r, etc.)
   * - No non-ASCII characters in local-part
   * - Local-part length: 1-64 characters
   * - No consecutive dots (..) in local-part or domain
   */
  strictEmail = (customMsg?: string): ValidatorFunction => {
    return (value?: string) => {
      if (!value) {
        return this.returnValidationResult(true, "strictEmail", customMsg);
      }

      const email = value.trim();

      // Rule a: Email must contain @
      if (!email.includes("@")) {
        return this.returnValidationResult(false, "emailNoAt", customMsg);
      }

      // Rule b: No multiple @ symbols
      const atCount = (email.match(/@/g) || []).length;
      if (atCount > 1) {
        return this.returnValidationResult(false, "emailMultipleAt", customMsg);
      }

      // Rule c: @ not at start or end (local-part or domain empty)
      const atIndex = email.indexOf("@");
      if (atIndex === 0 || atIndex === email.length - 1) {
        return this.returnValidationResult(false, "emailAtPosition", customMsg);
      }

      const localPart = email.substring(0, atIndex);
      const domain = email.substring(atIndex + 1);

      // Rule e: No control characters (\n, \t, \r, etc.) - must check before space check since \s matches these
      // eslint-disable-next-line no-control-regex
      if ((/[\x00-\x1F\x7F]/).test(email)) {
        return this.returnValidationResult(false, "emailControlChars", customMsg);
      }

      // Rule d: No spaces anywhere (only actual space character, not other whitespace)
      if ((/ /).test(value)) {
        return this.returnValidationResult(false, "emailSpaces", customMsg);
      }

      // Rule f: Local-part must be ASCII only (no Unicode/look-alike characters)
      // eslint-disable-next-line no-control-regex
      if (!(/^[\x20-\x7E]+$/).test(localPart)) {
        return this.returnValidationResult(false, "emailNonAscii", customMsg);
      }


      // Rule a: email local-part only allows: a-z, 0-9, .
      if (!(/^[a-z0-9.]+$/).test(localPart)) {
        return this.returnValidationResult(false, "emailInvalidChars", customMsg);
      }

      // Rule g: Local-part length must be 1-64 characters
      if (localPart.length < 1 || localPart.length > 64) {
        return this.returnValidationResult(false, "emailLocalPartLength", customMsg);
      }

      // Rule j: No consecutive dots in local-part or domain
      if ((/\.\./).test(localPart) || (/\.\./).test(domain)) {
        return this.returnValidationResult(false, "emailConsecutiveDots", customMsg);
      }

      return this.returnValidationResult(true, "strictEmail", customMsg);
    };
  };

  /**
   * Gmail-specific email validation.
   * In addition to strict rules, validates:
   * - Gmail local-part only allows: a-z, 0-9, .
   * - Gmail local-part length: 6-30 characters
   */
  gmailEmail = (customMsg?: string): ValidatorFunction => {
    return (value?: string) => {
      if (!value) {
        return this.returnValidationResult(true, "gmailEmail", customMsg);
      }

      const email = value.trim().toLowerCase();
      const atIndex = email.indexOf("@");

      // Only apply Gmail rules if domain is gmail.com or googlemail.com
      if (atIndex === -1) {
        return this.returnValidationResult(true, "gmailEmail", customMsg);
      }

      const domain = email.substring(atIndex + 1);
      if (domain !== "gmail.com" && domain !== "googlemail.com") {
        return this.returnValidationResult(true, "gmailEmail", customMsg);
      }

      const localPart = email.substring(0, atIndex);
      // Remove dots for validation (Gmail ignores dots)
      const localPartWithoutDots = localPart.replace(/\./g, "");

      // Rule b: Gmail local-part length: 6-30 characters (without dots)
      if (localPartWithoutDots.length < 6 || localPartWithoutDots.length > 30) {
        return this.returnValidationResult(false, "gmailLengthInvalid", customMsg);
      }

      return this.returnValidationResult(true, "gmailEmail", customMsg);
    };
  };

  /**
   * Fake phone number validation.
   * Detects suspicious phone numbers with repetitive digit patterns:
   * - All digits are the same (e.g., 666666666, 777777777)
   * - All digits after the first are the same (e.g., 611111111, 600000000)
   */
  fakePhoneNumber = (customMsg?: string): ValidatorFunction => {
    return (value?: string) => {
      if (!value) {
        return this.returnValidationResult(true, "fakePhoneNumber", customMsg);
      }

      // Remove any non-digit characters (spaces, dashes, etc.)
      const number = value.replace(/\D/g, "");

      // Empty after cleaning
      if (number.length === 0) {
        return this.returnValidationResult(true, "fakePhoneNumber", customMsg);
      }

      // Rule 1: All digits are the same (e.g., 666666666)
      if (number.split("").every((char) => char === number[0])) {
        return this.returnValidationResult(false, "fakePhoneNumber", customMsg);
      }

      // Rule 2: All digits after the first are the same (e.g., 611111111, 600000000)
      if (number.length > 1) {
        const restOfNumber = number.slice(1);
        if (restOfNumber.split("").every((char) => char === restOfNumber[0])) {
          return this.returnValidationResult(false, "fakePhoneNumber", customMsg);
        }
      }

      return this.returnValidationResult(true, "fakePhoneNumber", customMsg);
    };
  };

  url = (customMsg?: string): ValidatorFunction => {
    return (value?: string) => {
      const urlPattern =
        /* eslint-disable-next-line */
        /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;
      const isValid = urlPattern.test(value || "");
      return this.returnValidationResult(isValid, "url", customMsg);
    };
  };

  number = (customMsg?: string): ValidatorFunction => {
    return (value?: string | number) => {
      const isValid = (/^[-+]?\d+(\.\d+)?$/).test(String(value));
      return this.returnValidationResult(isValid, "number", customMsg);
    };
  };

  notNumber = (customMsg?: string): ValidatorFunction => {
    return (value?: string | number) => {
      const isValid = !(/^[-+]?\d+(\.\d+)?$/).test(String(value));
      return this.returnValidationResult(isValid, "notNumber", customMsg);
    };
  };

  notDigits = (customMsg?: string): ValidatorFunction => {
    return (value?: string | number) => {
      const isValid = !(/^\d+$/).test(String(value));
      return this.returnValidationResult(isValid, "notDigits", customMsg);
    };
  };

  integer = (customMsg?: string): ValidatorFunction => {
    return (value?: string | number) => {
      const isValid = (/^\d+$/).test(String(value));
      return this.returnValidationResult(isValid, "number", customMsg);
    };
  };

  sameAs = (value2: any, customMsg?: string): ValidatorFunction => {
    return (value?: any) => {
      const isValid = value === value2;
      return this.returnValidationResult(isValid, "sameAs", customMsg);
    };
  }

  includes = (value2: string, customMsg?: string): ValidatorFunction => {
    return (value?: string[]) => {
      const isValid = value?.includes(value2);
      return this.returnValidationResult(!!isValid, "includes", customMsg);
    };
  }

  notSameAs(value2: string, customMsg?: string) {
    return (value?: string) => {
      const isValid = value !== value2;
      return this.returnValidationResult(isValid, "notSameAs", customMsg);
    };
  }

  string = (customMsg?: string): ValidatorFunction => {
    return (value?: any) => {
      const isValid = _.isString(value);
      return this.returnValidationResult(isValid, "string", customMsg);
    };
  };

  dateValid = (
    inputFormats: string | string[],
    customMsg?: string
  ): ValidatorFunction => {
    return (value?: string) => {
      if (!value) {
        return this.returnValidationResult(true, "dateValid", customMsg);
      }
      const isValid = _.flow(
        (arr) => _.map(arr, (format) => DateService.isValid(value, format)),
        _.some
      )(_.toArray(inputFormats));
      return this.returnValidationResult(isValid, "dateValid", customMsg);
    };
  };

  dateRange = (
    minDate?: string,
    maxDate?: string,
    customMsg?: string
  ): ValidatorFunction => {
    return (value?: string) => {
      if (!value) {
        return this.returnValidationResult(true, "dateRange", customMsg);
      }
      if (!minDate || !maxDate) {
        return this.returnValidationResult(false, "dateRange", customMsg);
      }
      const selectedDateObj = DateService.parse(value);
      const isValid =
        selectedDateObj >= DateService.parse(minDate) &&
        selectedDateObj <= DateService.parse(maxDate);
      return this.returnValidationResult(isValid, "dateRange", customMsg);
    };
  };
}

const ValidationInstance = new Validation();
export default ValidationInstance;
